import {CommonModule} from '@angular/common';
import {Component, OnInit, computed, inject, output} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {ProductGroupsFacade} from '@em/data-feed/data-access-products';
import {
  DialogService,
  EmButtonModule,
  EmIconModule,
  EmInfoBoxModule,
  EmMoreInfoModule,
  EmPrimaryButtonComponent,
  EmRadioModule,
  EmTextButtonComponent,
} from '@em/shared/ui';
import {CountryCode} from '@em/shared/util-types';
import {
  CountryService,
  ScopedCountryService,
} from '@em/user-account/data-access';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {ManualProductSelectionComponent} from '../product-filter/manual-product-selection/manual-product-selection.component';

import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatBadgeModule} from '@angular/material/badge';
import {MatTabsModule} from '@angular/material/tabs';
import {
  ProductGroupEntity,
  ProductGroupFilterEntity,
} from '@em/data-feed/util-types';
import {
  CONDITION_FORM_BUILDER,
  ConditionalStatementList,
} from '@em/shared/mapping-engine/domain-models';
import {
  EmConditionalStatementListComponent,
  MappingEngineFormBuilderService,
} from '@em/shared/mapping-engine/feature';
import {ProductGroupFilterFormBuilderService} from '../filter-form-builder/filter-form-builder.service';
import {EmProductGroupFilterComponent} from '../product-group-filter/product-group-filter.component';
import {EmSelectProductGroupComponent} from '../select-product-group/select-product-group.component';

const HIGH_FREQUENCY_PRODUCT_LIMIT = 5;

@Component({
  selector: 'em-edit-product-group',
  templateUrl: './edit-product-group.component.html',
  styleUrls: ['./edit-product-group.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatTabsModule,
    MatBadgeModule,
    ManualProductSelectionComponent,
    EmPrimaryButtonComponent,
    EmButtonModule,
    EmRadioModule,
    EmTextButtonComponent,
    EmMoreInfoModule,
    EmConditionalStatementListComponent,
    EmProductGroupFilterComponent,
    EmIconModule,
    EmInfoBoxModule,
    EmSelectProductGroupComponent,
  ],
  providers: [
    MappingEngineFormBuilderService,
    ScopedCountryService,
    {
      provide: CONDITION_FORM_BUILDER,
      useClass: ProductGroupFilterFormBuilderService,
    },
  ],
})
export class EditProductGroupComponent implements OnInit {
  readonly #mappingEngineFormBuilder = inject(
    MappingEngineFormBuilderService<ProductGroupFilterEntity>,
  );
  readonly #fb = inject(UntypedFormBuilder);
  readonly #country = inject(CountryService);
  readonly #dialog = inject(DialogService);
  readonly #translate = inject(TranslateService);
  readonly #productGroupsFacade = inject(ProductGroupsFacade);
  readonly #scopedCountry = inject(ScopedCountryService);
  readonly data = inject<{group: ProductGroupEntity | undefined}>(
    MAT_DIALOG_DATA,
  );

  finishEditing = output<ProductGroupEntity | null>();

  readonly countryCode: Observable<CountryCode> = this.#country.observable();
  readonly productGroupsQuery = this.#productGroupsFacade.getGroups(undefined);
  readonly otherGroups = computed(() =>
    this.productGroupsQuery
      .data()
      ?.filter((g) => g.uuid !== this.data?.group?.uuid),
  );

  showExcludeInput = false;
  form: UntypedFormGroup;

  constructor() {
    this.countryCode.pipe(takeUntilDestroyed()).subscribe((countryCode) => {
      this.#scopedCountry.setCountryCode(countryCode);
    });

    this.form = this.#fb.group({
      uuid: [''],
      country: [''],
      name: ['', [Validators.required, Validators.minLength(1)]],
      excludeSetId: [''],
      products: {
        manuallyAdded: [],
        manuallyRemoved: [],
      },
      isGoogleSPD: [false, Validators.required],
      isPriceCrawl: [false, Validators.required],
    });
  }

  ngOnInit(): void {
    this.#initializeForm(this.data.group);
  }

  get excludeSetId() {
    return this.form.get('excludeSetId')?.value;
  }
  get conditionalStatementListControl(): FormGroup {
    return this.form.get('conditionalStatementList') as FormGroup;
  }

  get canSave(): boolean {
    return this.form.valid && this.form.value.name;
  }

  get filtersCount(): number {
    return (
      this.conditionalStatementListControl.value as ConditionalStatementList
    ).conditionalStatements.flatMap((statement) => statement.conditions).length;
  }

  get manualProductsCount(): {addedCount: number; removedCount: number} | null {
    const addedCount =
      this.form.get('products')?.value.manuallyAdded.length || 0;
    const removedCount =
      this.form.get('products')?.value.manuallyRemoved.length || 0;
    if (addedCount || removedCount) {
      return {
        addedCount,
        removedCount,
      };
    }
    return null;
  }

  updateExcludedGroup(groupId: string | undefined) {
    this.form.get('excludeSetId')?.setValue(groupId || null);
  }

  startAddingFilters() {
    // Fill with one empty condition statement to start with
    this.#mappingEngineFormBuilder.addNewConditionalStatementList(this.form);
  }

  saveGroup() {
    if (!this.canSave) return;
    const editedGroup: ProductGroupEntity = {
      uuid: this.form.value.uuid,
      name: this.form.value.name,
      excludeSetId: this.form.get('excludeSetId')?.value,
      country: this.form.value.country,
      manuallyAddedProducts: this.form.value.products.manuallyAdded,
      manuallyRemovedProducts: this.form.value.products.manuallyRemoved,
      productCount: null,
      appliedCampaigns: [],
      isGoogleSPD: this.form.value.isGoogleSPD,
      isPriceCrawl: this.form.value.isPriceCrawl,
      conditionalStatementList: this.form.value.conditionalStatementList,
    };
    this.finishEditing.emit(editedGroup);
  }

  close() {
    if (this.form.dirty) {
      this.#dialog
        .confirm({
          title: 'FILTER_SET_CONFIRM_TITLE',
          description: 'FILTER_SET_CONFIRM_TEXT',
          confirmLabel: 'SHARED_LABEL_DISCARD_CHANGES',
          cancelLabel: 'SHARED_LABEL_CONTINUE_EDITING',
          confirmType: 'danger',
        })
        .pipe(map((confirmed) => !!confirmed))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.finishEditing.emit(null);
          }
        });
    } else {
      this.finishEditing.emit(null);
    }
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  i18nSystemFilterSet(name: string | undefined): string {
    if (!name) return '';

    return this.#translate.instant(name.toUpperCase());
  }

  isHighFrequencyFilterSet(group: ProductGroupEntity | undefined): boolean {
    return group?.name === 'AUTOMATIC_FILTER_HIGH_FREQUENCY';
  }

  productLimit(group: ProductGroupEntity | undefined): null | number {
    return this.isHighFrequencyFilterSet(group)
      ? HIGH_FREQUENCY_PRODUCT_LIMIT
      : null;
  }

  #initializeForm(group: ProductGroupEntity | null = null) {
    // this.#scopedCountry.setCountryCode(group?.country ?? null);
    if (group) {
      this.form.patchValue({
        uuid: group?.uuid,
        country: group?.country,
        name: this.#translate.instant(group?.name), // For old templates the name is saved as i18n key
        excludeSetId: group?.excludeSetId,
        products: {
          manuallyAdded: group?.manuallyAddedProducts ?? [],
          manuallyRemoved: group?.manuallyRemovedProducts ?? [],
        },
        isGoogleSPD: group?.isGoogleSPD || false,
        isPriceCrawl: group?.isPriceCrawl || false,
      });
    }

    this.form.addControl(
      'conditionalStatementList',
      this.#mappingEngineFormBuilder.buildConditionalStatementList(
        group?.conditionalStatementList,
      ),
    );
  }
}
